<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import paymentMethodService, { type PaymentMethod, type PaymentProvider } from '../services/payment-method.service'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const clubUuid = route.params.uuid as string

const paymentMethod = ref<PaymentMethod | null>(null)
const config = ref<Record<string, any>>({})
const commission = ref<number>(0)
const loading = ref(false)
const loadingMethod = ref(true)
const error = ref<string | null>(null)
const stripeWebhookUrl = ref<string | null>(null)
const stripeWebhookUrlLoading = ref(false)
const stripeWebhookUrlCopied = ref(false)

const pageTitle = computed(() => {
  if (!paymentMethod.value) return t('paymentMethodEdit.editTitle')
  return t('paymentMethodEdit.editTitleProvider', { provider: paymentMethodService.getProviderName(paymentMethod.value.provider) })
})

const provider = computed(() => paymentMethod.value?.provider as PaymentProvider)

const sortedConfigKeys = computed(() => {
  const keys = Object.keys(config.value)
  if (provider.value === 'STRIPE') {
    const order = ['secret_key', 'publishable_key', 'webhook_secret', 'environment']
    return [...order.filter(k => keys.includes(k)), ...keys.filter(k => !order.includes(k))]
  }
  return keys
})

const copyStripeWebhookUrl = async () => {
  if (!stripeWebhookUrl.value) return
  await navigator.clipboard.writeText(stripeWebhookUrl.value)
  stripeWebhookUrlCopied.value = true
  setTimeout(() => { stripeWebhookUrlCopied.value = false }, 2000)
}

const loadPaymentMethod = async () => {
  loadingMethod.value = true
  error.value = null

  try {
    const response = await paymentMethodService.getPaymentMethod(clubUuid)
    paymentMethod.value = response.payment_method
    config.value = { ...response.payment_method.configuration }
    commission.value = response.payment_method.commission || 0

    if (response.payment_method.provider === 'STRIPE') {
      stripeWebhookUrlLoading.value = true
      try {
        stripeWebhookUrl.value = await paymentMethodService.getStripeWebhookUrl()
      } catch {
        // silently ignore
      } finally {
        stripeWebhookUrlLoading.value = false
      }
    }
  } catch (err: any) {
    console.error('Error loading payment method:', err)
    error.value = err.response?.data?.message || t('paymentMethodEdit.errorLoading')
  } finally {
    loadingMethod.value = false
  }
}

const handleSave = async () => {
  error.value = null

  if (!provider.value) {
    error.value = t('paymentMethodEdit.noProvider')
    return
  }

  const validationError = paymentMethodService.validateConfiguration(provider.value, config.value)
  if (validationError) {
    error.value = validationError
    return
  }

  loading.value = true

  try {
    await paymentMethodService.updatePaymentMethod(clubUuid, {
      configuration: config.value,
      commission: commission.value
    })

    router.push(`/club/${clubUuid}/payment-settings`)
  } catch (err: any) {
    console.error('Error updating payment method:', err)
    error.value = err.response?.data?.message || t('paymentMethodEdit.errorSaving')
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  router.push(`/club/${clubUuid}/payment-settings`)
}

onMounted(() => {
  loadPaymentMethod()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-6">
      <!-- Back Button -->
      <div class="mb-4">
        <button
          @click="handleCancel"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('paymentMethodEdit.backToSettings') }}
        </button>
      </div>

      <!-- Page Header -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900">{{ pageTitle }}</h1>
      </div>

      <!-- Loading State -->
      <div v-if="loadingMethod" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <div class="flex items-center justify-center py-12">
          <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p class="text-gray-600">{{ t('paymentMethodEdit.loading') }}</p>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error && !paymentMethod" class="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        <p class="text-sm">{{ error }}</p>
        <button
          @click="handleCancel"
          class="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
        >
          {{ t('paymentMethodEdit.backToSettingsShort') }}
        </button>
      </div>

      <!-- Form -->
      <div v-else-if="paymentMethod" class="space-y-4">
        <!-- Error Message (during save) -->
        <div v-if="error" class="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          <p class="text-sm">{{ error }}</p>
        </div>

        <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
          <form @submit.prevent="handleSave" class="space-y-6">
            <!-- Commission Field -->
            <div>
              <label for="commission" class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('paymentMethodEdit.commission') }}
              </label>
              <input
                id="commission"
                v-model.number="commission"
                type="number"
                min="0"
                max="100"
                step="0.01"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p class="mt-1 text-xs text-gray-600">
                {{ t('paymentMethodEdit.commissionHint') }}
              </p>
            </div>

            <!-- Dynamic form fields based on provider -->
            <template v-for="key in sortedConfigKeys" :key="key">
              <div v-if="key !== 'provider'">
                <label :for="key" class="block text-sm font-medium text-gray-700 mb-2">
                  {{ key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }}
                  <span class="text-red-600">*</span>
                </label>

                <!-- Select for environment/region -->
                <select
                  v-if="key === 'environment' || key === 'region'"
                  :id="key"
                  v-model="config[key]"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <template v-if="key === 'environment'">
                    <option v-if="provider === 'PAYU' || provider === 'PAYPAL'" value="sandbox">{{ t('paymentMethodCreate.sandboxTest') }}</option>
                    <option v-if="provider === 'PAYU' || provider === 'PAYPAL'" value="production">{{ t('paymentMethodCreate.productionLive') }}</option>
                    <option v-if="provider === 'STRIPE'" value="test">{{ t('paymentMethodCreate.testEnv') }}</option>
                    <option v-if="provider === 'STRIPE'" value="live">{{ t('paymentMethodCreate.liveEnv') }}</option>
                    <option v-if="provider === 'PRZELEWY24'" value="sandbox">{{ t('paymentMethodCreate.sandboxTest') }}</option>
                    <option v-if="provider === 'PRZELEWY24'" value="production">{{ t('paymentMethodCreate.productionLive') }}</option>
                    <option v-if="provider === 'KLARNA'" value="playground">{{ t('paymentMethodCreate.playgroundEnv') }}</option>
                    <option v-if="provider === 'KLARNA'" value="production">{{ t('paymentMethodCreate.productionLive') }}</option>
                  </template>
                  <template v-else-if="key === 'region'">
                    <option value="eu">{{ t('paymentMethodCreate.europeRegion') }}</option>
                    <option value="na">{{ t('paymentMethodCreate.northAmericaRegion') }}</option>
                    <option value="oc">{{ t('paymentMethodCreate.oceaniaRegion') }}</option>
                  </template>
                </select>

                <!-- Regular text input for all other fields -->
                <input
                  v-else
                  :id="key"
                  v-model="config[key]"
                  type="text"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />

                <!-- Stripe webhook info block shown after webhook_secret field -->
                <div v-if="key === 'webhook_secret' && provider === 'STRIPE'" class="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p class="text-sm font-medium text-blue-900 mb-1">{{ t('paymentMethodCreate.stripeWebhookTitle') }}</p>
                  <p class="text-xs text-blue-800 mb-3">{{ t('paymentMethodCreate.stripeWebhookDescription') }}</p>
                  <div class="mb-3">
                    <p class="text-xs font-medium text-blue-900 mb-1">{{ t('paymentMethodCreate.stripeWebhookUrl') }}</p>
                    <div class="flex items-center gap-2">
                      <code v-if="stripeWebhookUrl" class="flex-1 text-xs bg-white border border-blue-200 rounded px-2 py-1.5 text-gray-800 break-all">{{ stripeWebhookUrl }}</code>
                      <span v-else class="flex-1 text-xs text-blue-600 italic">{{ stripeWebhookUrlLoading ? t('paymentMethodCreate.stripeWebhookUrlLoading') : '—' }}</span>
                      <button
                        v-if="stripeWebhookUrl"
                        type="button"
                        @click="copyStripeWebhookUrl"
                        class="shrink-0 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        {{ stripeWebhookUrlCopied ? t('paymentMethodCreate.stripeWebhookUrlCopied') : t('paymentMethodCreate.stripeWebhookUrlCopy') }}
                      </button>
                    </div>
                  </div>
                  <div>
                    <p class="text-xs font-medium text-blue-900 mb-1">{{ t('paymentMethodCreate.stripeWebhookEvents') }}</p>
                    <ul class="text-xs text-blue-800 space-y-0.5 list-disc list-inside">
                      <li>payment_intent.succeeded</li>
                      <li>payment_intent.payment_failed</li>
                      <li>payment_intent.canceled</li>
                      <li>checkout.session.completed</li>
                      <li>checkout.session.expired</li>
                    </ul>
                  </div>
                </div>
              </div>
            </template>

            <!-- Action Buttons -->
            <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                @click="handleCancel"
                :disabled="loading"
                class="px-6 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ t('common.cancel') }}
              </button>
              <button
                type="submit"
                :disabled="loading"
                class="px-6 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ loading ? t('paymentMethodEdit.saving') : t('paymentMethodEdit.saveChanges') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </main>
</template>
